"use client";

import type {
  BillingInfo,
  TeamMembersResponse,
  TenantProfile,
  TenantSummary,
  UpdateTenantProfilePayload,
} from "./types";
import { getTokenFromMsal } from "@/auth/msalTokenProvider";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

function getOrCreateId(key: string): string {
  if (typeof window === "undefined") return "server";
  const existing = window.localStorage.getItem(key);
  if (existing) return existing;
  const created =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  window.localStorage.setItem(key, created);
  return created;
}

function getVisitorId(): string {
  return getOrCreateId("r365_visitor_id");
}

function getSessionId(): string {
  if (typeof window === "undefined") return "server";
  const existing = window.sessionStorage.getItem("r365_session_id");
  if (existing) return existing;
  const created =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  window.sessionStorage.setItem("r365_session_id", created);
  return created;
}

/** Get id_token for backend API calls: MSAL first (frontend auth), then /auth/token (backend session). */
async function getIdToken(): Promise<string | null> {
  const msalToken = await getTokenFromMsal();
  if (msalToken) return msalToken;
  const res = await fetch(`${API_BASE}/auth/token`, { credentials: "include" });
  if (!res.ok) return null;
  const data = (await res.json()) as { idToken?: string };
  return data.idToken ?? null;
}

/** Try to refresh session; returns new idToken or null. Invisible to the user. */
async function refreshToken(): Promise<string | null> {
  const res = await fetch(`${API_BASE}/auth/refresh`, { credentials: "include" });
  if (!res.ok) return null;
  const data = (await res.json()) as { idToken?: string };
  return data.idToken ?? null;
}

async function fetchApi<T>(
  path: string,
  init?: RequestInit,
  options?: { _retried?: boolean }
): Promise<T> {
  let idToken = await getIdToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...init?.headers,
  };
  if (idToken) {
    (headers as Record<string, string>)["Authorization"] = `Bearer ${idToken}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });

  if (res.status === 401 && !options?._retried) {
    const newToken = await refreshToken();
    if (newToken) {
      const retryHeaders: HeadersInit = {
        "Content-Type": "application/json",
        ...init?.headers,
        Authorization: `Bearer ${newToken}`,
      };
      const retryRes = await fetch(`${API_BASE}${path}`, {
        ...init,
        credentials: "include",
        headers: retryHeaders,
      });
      if (!retryRes.ok) {
        const text = await retryRes.text();
        const err = new Error(
          retryRes.status === 401 ? "Unauthorized" : text || `HTTP ${retryRes.status}`
        ) as Error & { status: number };
        err.status = retryRes.status;
        throw err;
      }
      return retryRes.json() as Promise<T>;
    }
  }

  if (!res.ok) {
    const text = await res.text();
    const err = new Error(res.status === 401 ? "Unauthorized" : text || `HTTP ${res.status}`) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return res.json() as Promise<T>;
}

/** Thrown by fetchApi; has .status for 404 etc. */
export type ApiError = Error & { status: number };

export type SessionUser = { email: string; name: string; id: string; entraTenantId: string };

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(payload);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Current user from the id_token (MSAL). No API call; reads email, name, id, entraTenantId from token claims.
 */
export async function getSessionUser(): Promise<SessionUser | null> {
  const token = await getIdToken();
  if (!token) return null;
  const claims = decodeJwtPayload(token);
  if (!claims) return null;
  // For Entra users, oid is the stable user object GUID expected by tenant APIs.
  const oid = claims.oid as string | undefined;
  const sub = claims.sub as string | undefined;
  const userId = oid ?? sub;
  const tid = claims.tid as string | undefined;
  const name =
    (claims.name as string) ??
    (claims.preferred_username as string) ??
    null;
  const email =
    (claims.email as string) ??
    (claims.upn as string) ??
    (claims.preferred_username as string) ??
    null;
  if (!userId || !tid) return null;
  return {
    id: userId,
    entraTenantId: tid,
    name: name ?? "",
    email: email ?? "",
  };
}

export type Plan = { id: string; title: string };

/**
 * Fetches plans for enrollment (map selectedPlan key to plan id).
 */
export async function getPlans(): Promise<Plan[]> {
  return fetchApi<Plan[]>("/plans");
}

export type CreateTenantDto = {
  entraTenantId: string;
  name: string;
  planId: string;
  entraUserId: string;
  email?: string;
};

/**
 * Creates a tenant (enrollment integrate). Uses session token.
 */
export async function createTenant(dto: CreateTenantDto): Promise<unknown> {
  return fetchApi<unknown>("/tenants", {
    method: "POST",
    body: JSON.stringify(dto),
  });
}

/**
 * Fetches the tenant summary for the current user (session cookie is sent).
 * The dashboard API route will attach the auth token and call the backend.
 */
export async function getTenantSummary(): Promise<TenantSummary> {
  return fetchApi<TenantSummary>("/tenants");
}

/**
 * Updates company name, address, and primary contact (dashboard backoffice).
 */
export async function updateTenantProfile(
  body: UpdateTenantProfilePayload
): Promise<TenantProfile> {
  return fetchApi<TenantProfile>("/tenants/profile", {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export type LicensedUsersResponse = { licensedUserCount: number };

/**
 * Fetches the licensed user count for the current user's Entra tenant.
 */
export async function getLicensedUserCount(): Promise<LicensedUsersResponse> {
  return fetchApi<LicensedUsersResponse>("/tenants/licensed-users");
}

/**
 * Fetches billing info (plan, price, storage) for the current user's tenant.
 */
export async function getBilling(): Promise<BillingInfo> {
  return fetchApi<BillingInfo>("/tenants/billing");
}

/**
 * Fetches team members for the current user's tenant.
 */
export async function getTeamMembers(): Promise<TeamMembersResponse> {
  return fetchApi<TeamMembersResponse>("/tenants/team-members");
}

/** Booking availability slot (ISO 8601 start/end). */
export type AvailabilitySlot = {
  startDateTime: string;
  endDateTime: string;
};

/**
 * Fetches available booking slots from the calendar (public endpoint, no auth).
 */
export async function getBookingAvailability(): Promise<{ slots: AvailabilitySlot[] }> {
  const res = await fetch(`${API_BASE}/booking/availability`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json() as Promise<{ slots: AvailabilitySlot[] }>;
}

export type BookMeetingParams = {
  startDateTime: string;
  endDateTime: string;
  attendeeEmail: string;
  attendeePhone?: string;
  language?: string;
};

/**
 * Books a 30 min Teams meeting and invites the attendee (public endpoint, no auth).
 */
export async function bookMeeting(params: BookMeetingParams): Promise<{ id: string; webLink?: string }> {
  const res = await fetch(`${API_BASE}/booking/book`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(params),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = typeof data?.message === "string" ? data.message : data?.error ?? res.statusText ?? `HTTP ${res.status}`;
    throw new Error(message);
  }
  return data as { id: string; webLink?: string };
}

export type AnalyticsPageId = "home" | "book" | "event" | "enrollment" | "sovereign_continuity";

export type AnalyticsEventType =
  | "page_view"
  | "booking_started"
  | "booking_booked"
  | "booking_canceled"
  | "resilience_test_started"
  | "resilience_test_completed";

export type AnalyticsMetadata = Record<string, string | number | boolean>;

function stableMetadataKey(metadata?: AnalyticsMetadata): string {
  if (!metadata || Object.keys(metadata).length === 0) return "";
  const keys = Object.keys(metadata).sort();
  return JSON.stringify(keys.map((k) => [k, metadata[k]]));
}

/** Suppress duplicate sends (e.g. React StrictMode double effects, duplicate hooks). */
const analyticsDedupeUntil = new Map<string, number>();
const ANALYTICS_DEDUPE_MS = 3500;

function shouldSkipDuplicateAnalytics(
  eventType: AnalyticsEventType,
  page: string | undefined,
  metadataKey: string
): boolean {
  if (typeof window === "undefined") return false;
  const key = `${eventType}:${page ?? ""}:${window.location.pathname}:${metadataKey}`;
  const now = Date.now();
  const until = analyticsDedupeUntil.get(key) ?? 0;
  if (now < until) return true;
  analyticsDedupeUntil.set(key, now + ANALYTICS_DEDUPE_MS);
  return false;
}

/** Map current route to a marketing / flow page label for analytics. */
export function analyticsPageFromPath(pathname: string): AnalyticsPageId | undefined {
  const p = (pathname.split("?")[0] || "/").replace(/\/$/, "") || "/";
  if (p === "/") return "home";
  if (p.startsWith("/book")) return "book";
  if (p.startsWith("/event")) return "event";
  if (p.startsWith("/enrollment")) return "enrollment";
  if (p.startsWith("/sovereign-continuity")) return "sovereign_continuity";
  return undefined;
}

export async function trackAnalyticsEvent(
  eventType: AnalyticsEventType,
  page?: AnalyticsPageId,
  metadata?: AnalyticsMetadata
): Promise<void> {
  const metaKey = stableMetadataKey(metadata);
  if (shouldSkipDuplicateAnalytics(eventType, page, metaKey)) return;
  const payload: Record<string, unknown> = {
    eventType,
    page,
    visitorId: getVisitorId(),
    sessionId: getSessionId(),
  };
  if (metadata && Object.keys(metadata).length > 0) {
    payload.metadata = metadata;
  }
  // Fire-and-forget for UX safety.
  void fetch(`${API_BASE}/analytics/track`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  }).catch(() => undefined);
}

export type SendResiliencePlanParams = {
  email: string;
  name?: string;
  companySize?: string;
  wantsContact: boolean;
  lang: "fr" | "en";
  scorePercent: number;
  /** Exactly 5 question/answer pairs (localized question text from the client) */
  questionAnswers: Array<{ question: string; answer: "yes" | "no" }>;
};

export async function sendResiliencePlanByEmail(params: SendResiliencePlanParams): Promise<void> {
  const body = {
    ...params,
    visitorId: getVisitorId(),
    sessionId: getSessionId(),
  };
  const res = await fetch(`${API_BASE}/resilience-plan/send`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = typeof data?.message === "string" ? data.message : data?.error ?? res.statusText ?? `HTTP ${res.status}`;
    throw new Error(message);
  }
}

export type AnalyticsSummary = {
  pageViews: number;
  uniquePageViews: number;
  bookingStarted: number;
  bookingsBooked: number;
};

export async function getAnalyticsSummary(): Promise<AnalyticsSummary> {
  return fetchApi<AnalyticsSummary>("/analytics/summary");
}
